
import { VERSION_METADATA as LANDING_VERSION_METADATA } from '@/components/landing/componentVersions';
import { VERSION_METADATA as APP_VERSION_METADATA, VERSION_REGISTRY } from '@/versioning/versionRegistry';

// Core version information - now independent of package.json
export const UNIFIED_VERSION_INFO = {
  // App core version (from internal versioning system)
  app: {
    version: '2.2.0',
    status: 'wip',
    metadata: APP_VERSION_METADATA
  },
  
  // Landing page version (from component versioning)
  landing: {
    version: '4.0',
    status: 'working',
    metadata: LANDING_VERSION_METADATA
  },
  
  // Internal version (decoupled from package.json since it's read-only)
  package: '2.2.0-wip',
  
  // Build information
  build: {
    date: new Date().toISOString(),
    environment: import.meta.env.MODE || 'development',
    vite: '2.2.0-wip', // Using internal version
    packageJsonReadOnly: true,
    internalVersioning: true
  }
};

export interface UnifiedVersionInfo {
  app: {
    version: string;
    status: string;
    fullVersion: string;
    pageVersions: {
      dashboard: string;
      candidates: string;
      jobs: string;
      clients: string;
      leads: string;
      pipeline: string;
      calendar: string;
      analytics: string;
      settings: string;
      messaging: string;
      automation: string;
      aiHub: string;
      betaProgram: string;
    };
  };
  landing: {
    version: string;
    status: string;
    lastUpdated: string;
  };
  system: {
    package: string;
    build: string;
    environment: string;
    browser: string;
    timestamp: string;
    versioningNote: string;
  };
}

export const getUnifiedVersionInfo = (): UnifiedVersionInfo => {
  const userAgent = navigator.userAgent;
  const browser = getBrowserInfo(userAgent);
  
  return {
    app: {
      version: UNIFIED_VERSION_INFO.app.version,
      status: UNIFIED_VERSION_INFO.app.status,
      fullVersion: `${UNIFIED_VERSION_INFO.app.version}-${UNIFIED_VERSION_INFO.app.status}`,
      pageVersions: {
        dashboard: VERSION_REGISTRY.dashboard,
        candidates: VERSION_REGISTRY.candidates,
        jobs: VERSION_REGISTRY.jobs,
        clients: VERSION_REGISTRY.clients,
        leads: VERSION_REGISTRY.leads,
        pipeline: VERSION_REGISTRY.pipeline,
        calendar: VERSION_REGISTRY.calendar,
        analytics: VERSION_REGISTRY.analytics,
        settings: VERSION_REGISTRY.settings,
        messaging: VERSION_REGISTRY.messaging,
        automation: VERSION_REGISTRY.automation,
        aiHub: VERSION_REGISTRY.aiHub,
        betaProgram: VERSION_REGISTRY.betaProgram,
      }
    },
    landing: {
      version: UNIFIED_VERSION_INFO.landing.version,
      status: UNIFIED_VERSION_INFO.landing.status,
      lastUpdated: LANDING_VERSION_METADATA.lastUpdated,
    },
    system: {
      package: UNIFIED_VERSION_INFO.package,
      build: UNIFIED_VERSION_INFO.build.date,
      environment: UNIFIED_VERSION_INFO.build.environment,
      browser: browser,
      timestamp: new Date().toISOString(),
      versioningNote: 'Using internal versioning (package.json is read-only)'
    }
  };
};

export const getVersionSummary = (): string => {
  const info = getUnifiedVersionInfo();
  return `InvenioCRM v${info.app.fullVersion} (Landing: ${info.landing.version})`;
};

export const getSupportVersionInfo = (): string => {
  const info = getUnifiedVersionInfo();
  
  return `InvenioCRM Version Information
=================================

App Version: ${info.app.fullVersion}
Landing Page: v${info.landing.version}
Internal Package Version: ${info.system.package}

System Information:
- Environment: ${info.system.environment}
- Browser: ${info.system.browser}
- Build Date: ${info.system.build}
- Report Generated: ${info.system.timestamp}
- Versioning: ${info.system.versioningNote}

Page Versions:
- Dashboard: ${info.app.pageVersions.dashboard}
- Candidates: ${info.app.pageVersions.candidates}
- Jobs: ${info.app.pageVersions.jobs}
- Clients: ${info.app.pageVersions.clients}
- Leads: ${info.app.pageVersions.leads}
- Pipeline: ${info.app.pageVersions.pipeline}
- Calendar: ${info.app.pageVersions.calendar}
- Analytics: ${info.app.pageVersions.analytics}
- Settings: ${info.app.pageVersions.settings} (NEW: Version synchronization)
- Messaging: ${info.app.pageVersions.messaging}
- Automation: ${info.app.pageVersions.automation}
- AI Hub: ${info.app.pageVersions.aiHub}
- Beta Program: ${info.app.pageVersions.betaProgram}

Version 2.2.0-wip Features:
- Enhanced onboarding flow with improved authentication handling
- Better organization setup and member management
- Conservative cleanup system for edge cases
- Robust redirect logic and error recovery
- Improved user experience for stuck onboarding states
- Internal versioning system (package.json read-only workaround)`;
};

const getBrowserInfo = (userAgent: string): string => {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
};

export const copyVersionInfoToClipboard = async (): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(getSupportVersionInfo());
    return true;
  } catch (error) {
    console.error('Failed to copy version info:', error);
    return false;
  }
};
